import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconoirXmark } from '@ey-xd/motif-icon';
import { MOTIF_MODAL_DATA, MotifModalRef, MotifModule } from '@ey-xd/ng-motif';
import { PrivacyNoticeDto } from '@models/webapi/privacy-notice/privacy-notice.model';
import { TranslatePipe } from '@ngx-translate/core';
import { PrivacyNoticeService } from '@services/webapi/privacy-notice.service';
import { tap } from 'rxjs';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [MotifModule, NgOptimizedImage, NgTemplateOutlet, TranslatePipe],
  templateUrl: './privacy-policy.component.html',
  styleUrls: ['./privacy-policy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivacyPolicyComponent implements AfterViewInit {
  @ViewChild('policyText') policyText!: ElementRef<HTMLDivElement>;

  public readonly modalCloseIcon = IconoirXmark;
  public readonly data = inject<PrivacyPolicyComponentData>(MOTIF_MODAL_DATA);
  readonly #privacyNotice: PrivacyNoticeDto = this.data.privacyNotice;
  readonly #variant: 'acknowledge' | 'show' = this.data.variant;
  public imageURL = signal<string>('');
  #isScrolledToBottom = false;
  #ackClicked = false;
  #destroyRef: DestroyRef = inject(DestroyRef);
  #dialogRef = inject(MotifModalRef<PrivacyPolicyComponent, boolean>);
  #privacyNoticeService = inject(PrivacyNoticeService);

  ngAfterViewInit() {
    if (this.isAcknowledgeVariant) {
      this.policyText.nativeElement.addEventListener('scroll', () => this.onScroll.bind(this));
      this.onScroll();
    }

    this.#updateImageURL();
    window.addEventListener('resize', this.#updateImageURL.bind(this));
  }

  public onScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.policyText.nativeElement;

    if (Math.ceil(scrollTop) + clientHeight >= scrollHeight) {
      this.#isScrolledToBottom = true;
    }
  }

  public onAcknowledge() {
    if (this.isAckButtonDisabled) {
      return;
    }

    this.#ackClicked = true;

    this.#privacyNoticeService
      .acknowledgePrivacyNotice(this.#privacyNotice.version!)
      .pipe(
        tap(() => {
          this.#dialogRef.close(true);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public onCloseClicked() {
    this.#dialogRef.close();
  }

  public get privacyNoticeName(): string | null {
    return this.#privacyNotice.name;
  }

  public get privacyNoticeBody(): string | null {
    return this.#privacyNotice.bodyMultiline;
  }

  public get isAcknowledgeVariant(): boolean {
    return this.#variant === 'acknowledge';
  }

  public get isAckButtonDisabled(): boolean {
    return (
      this.#variant === 'show' || !this.#isScrolledToBottom || this.#ackClicked || this.#privacyNotice.version === null
    );
  }

  #updateImageURL(): void {
    const width = window.innerWidth;
    let imagePath = '/assets/images/eytc-narrow-background-1366px.png';
    if (width >= 1600) {
      imagePath = '/assets/images/eytc-narrow-background-1600px.png';
    }
    this.imageURL.set(imagePath);
  }
}

export class PrivacyPolicyComponentData {
  public constructor(
    public privacyNotice: PrivacyNoticeDto,
    public variant: 'acknowledge' | 'show' = 'acknowledge'
  ) {}
}
